import { useCallback, useEffect, useRef, useState } from "react";
import { PendingBudgetTransaction, PermissionCategory } from "../../../http/protocol";
import { t } from "../../../i18n";
import { getContainer } from "../../../ioc/IOCSetup";
import { PagerModel } from "../../list/list/PagerModel";
import { PermissionRequired } from "../../security/PermissionRequired";
import { AdminTransactionConfirmModal, TransactionActionType } from "./AdminTransactionConfirmModal";
import AdminTransactionsTable from "./AdminTransactionsTable";
import { ComponentSpinner } from "../../ComponentSpinner";
import "./adminTransactionsListView.scss";

function AdminTransactionsListView() {
  const [transactions, setTransactions] = useState<PendingBudgetTransaction[]>([]);
  const [pager, setPager] = useState<PagerModel>(() => {
    const initialPager = new PagerModel();
    initialPager.limit = 50;
    return initialPager;
  });
  const [pendingAction, setPendingAction] = useState<{
    transaction: PendingBudgetTransaction;
    mode: TransactionActionType;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  const fetchTransactions = useCallback(async (offset: number, limit: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getContainer().getSupervisorService().getPendingTransactions({ offset, limit });
      if (!mounted.current) return;
      const items = response.items ?? [];
      setTransactions(items);
      setPager((prev) => new PagerModel(prev, items.length));
    } catch (_) {
      if (mounted.current) setError(t("adminTransactions.error.fetch"));
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTransactions(pager.offset, pager.limit);
  }, [fetchTransactions, pager.offset, pager.limit]);

  const handleAction = (item: PendingBudgetTransaction, rejectOrApprove: TransactionActionType): void => {
    setPendingAction({ transaction: item, mode: rejectOrApprove });
  };

  const handleConfirm = async (reason: string) => {
    if (!pendingAction || pendingAction.transaction.id == null) return;
    const { id } = pendingAction.transaction;
    const service = getContainer().getSupervisorService();
    try {
      if (pendingAction.mode === "approve") {
        await service.approvePendingTransaction(id);
      } else {
        await service.rejectPendingTransaction(id, { reason });
      }
      setPendingAction(null);
      fetchTransactions(pager.offset, pager.limit);
    } catch (_) {
      setError(t("adminTransactions.error.action"));
    }
  };

  return (
    <PermissionRequired
      permissions={[PermissionCategory.SUPERVISOR]}
      accessLevel="r"
      showError={true}
      displayBox={true}
    >
      <div>
        <h1 className="transactions-title">{t("adminTransactions.pageTitle")}</h1>
        <div className="card-box">
          {error && <p className="text-danger">{error}</p>}
          {loading ? (
            <ComponentSpinner />
          ) : transactions.length === 0 ? (
            <p>{t("adminTransactions.empty")}</p>
          ) : (
            <AdminTransactionsTable
              transactions={transactions}
              pager={pager}
              setPager={setPager}
              handleAction={handleAction}
            />
          )}
          <AdminTransactionConfirmModal
            mode={pendingAction?.mode ?? null}
            onConfirm={handleConfirm}
            onCancel={() => setPendingAction(null)}
          />
        </div>
      </div>
    </PermissionRequired>
  );
}

export default AdminTransactionsListView;
